import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MembersCategory } from '../../../../../models/interfaces/members_category.interface';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { CustomFile } from '../../../../../models/interfaces/custom_file.interface';
import { RemoteFile } from '../../../../../models/classes/remote_file.model';
import { Subject, firstValueFrom, takeUntil } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { AlertService } from '../../../../../core/services/alert/alert.service';
import { ApiService } from '../../../../../core/services/api/api.service';
import { ToastService } from '../../../../../core/services/toast/toast.service';
import { UtilsService } from '../../../../../shared/utils/utils.service';
import { cloneDeep } from 'lodash-es';
import { v4 as uuidv4 } from 'uuid';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { EnfafDropzoneComponent } from '../../../../../components/enfaf-dropzone/enfaf-dropzone.component';
import { EnfafRemoteAttachmentsComponent } from '../../../../../components/enfaf-remote-attachments/enfaf-remote-attachments.component';

@Component({
    selector: 'app-modal-edit-members-category',
    imports: [
        FormsModule,
        ReactiveFormsModule,
        EnfafDropzoneComponent,
        EnfafRemoteAttachmentsComponent,
        MatDialogModule,
        MatIconModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatSlideToggleModule,
        MatProgressSpinnerModule,
        MatSelectModule,
        MatOptionModule
    ],
    templateUrl: './modal-edit-members-category.component.html',
    styleUrl: './modal-edit-members-category.component.scss'
})
export class ModalEditMembersCategoryComponent implements OnInit, OnDestroy {
  isLoadingCategory = true;
  receivedCategory!: MembersCategory;
  categoryForm: FormGroup = new FormGroup({});
  editMode = false;
  showCloseAlert = false;
  isSavingCategory = false;
  currentCategories: Array<MembersCategory> = [];
  imageToUpload: File | null = null;
  imageToUploadSrc: string | null = null;
  currentThumbnail!: string;
  currentPosition!: number;
  filesToUpload: Array<CustomFile> = [];
  currentAttachments: Array<RemoteFile> = [];
  isDeletingRemoteAttachment = false;

  private unsubscribe$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<ModalEditMembersCategoryComponent>,
    private alert: AlertService,
    private api: ApiService,
    private toast: ToastService,
    private utils: UtilsService,
    private changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    const receivedData = this.data ? cloneDeep(this.data) : null;
    this.currentCategories = receivedData?.currentCategories || [];
    const membersCategoryId = receivedData?.membersCategoryId;
    if (membersCategoryId) {
      this.fetchMembersCategory(membersCategoryId);
    } else {
      this.isLoadingCategory = false;
      this.initForm();
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  async confirmCloseModal(): Promise<any> {
    if (this.showCloseAlert) {
      const closeModal = await this.alert.presentCloseWindowAlert();
      return closeModal ? this.closeModal() : null;
    }
    this.closeModal();
  }

  closeModal(result?: any): void {
    this.dialogRef.close(result);
  }

  async fetchMembersCategory(membersCategoryId: string): Promise<void> {
    try {
      const params = { membersCategoryId };
      const response = await firstValueFrom(this.api.fetchMembersCategory(params));
      this.isLoadingCategory = false;
      this.receivedCategory = response.category;
      this.initForm();
    } catch (errorResponse: any) {
      this.isLoadingCategory = false;
      this.initForm();
      const errorMessage = errorResponse?.error?.message;
      this.toast.presentErrorToast(errorMessage || 'Hubo un error buscando categoría');
    }
  }

  initForm(): void {
    if (this.receivedCategory) {
      this.editMode = true;
      this.currentThumbnail = this.receivedCategory.thumbnailUrl;
      this.currentPosition = this.receivedCategory.number;
      this.currentAttachments = this.receivedCategory.attachments;
    }
    this.categoryForm = new FormGroup({
      membersCategoryId: new FormControl(this.receivedCategory?.membersCategoryId || uuidv4(), Validators.required),
      position: new FormControl(this.receivedCategory?.number || 0),
      name: new FormControl(this.receivedCategory?.name || null, Validators.required),
      hidden: new FormControl(this.receivedCategory?.hidden || false, Validators.required)
    });
    this.listenForChanges();
  }

  listenForChanges(): void {
    this.categoryForm.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((value) => {
      this.showCloseAlert = true;
    });
  }

  async onSelectImage(event: any) {
    try {
      const imgFile: File = event.target.files && event.target.files[0];
      if (!imgFile) return;
      if (imgFile.size > this.utils.maxImageSize) {
        (document.getElementById('categoryThumbnail') as HTMLInputElement).value = '';
        const fileName = imgFile.name;
        const errorMessage = `La imagen '${fileName}' no puede exceder los ${this.utils.maxImageSize / this.utils.oneMegaByte} MB\n`;
        this.toast.presentErrorToast(errorMessage);
        return;
      }
      this.imageToUploadSrc = await this.utils.previewImageFile(imgFile);
      if (!this.imageToUploadSrc) return;
      this.imageToUpload = imgFile;
      this.changeDetectorRef.detectChanges();
    } catch (error) {
      this.toast.presentErrorToast('Hubo un error adjuntando imagen');
    }
  }

  onDroppedAttachments(files: Array<CustomFile>): void {
    if (!files?.length) return;
    for (const file of files) {
      file.name = this.utils.generateFileName(file.name);
    }
    this.filesToUpload = files;
  }

  async saveCategory(): Promise<void> {
    try {
      this.isSavingCategory = true;
      const categoryFormData = new FormData();
      const formValue = this.categoryForm.value;
      categoryFormData.append('form', JSON.stringify(formValue));
      if (this.imageToUpload) {
        categoryFormData.append('thumbnail', this.imageToUpload, this.imageToUpload.name);
      }
      if (this.filesToUpload.length) {
        for (const [fileIndex, fileValue] of this.filesToUpload.entries()) {
          categoryFormData.append(`attachment_${fileIndex}`, fileValue.file, fileValue.name);
        }
      }
      const response = await firstValueFrom(this.api.saveMembersCategory(categoryFormData));
      this.isSavingCategory = false;
      this.toast.presentSuccessToast(response.message);
      this.closeModal({ course: response.course, categories: response.categories });
    } catch (errorResponse: any) {
      this.isSavingCategory = false;
      const errorMessage = errorResponse?.error?.message;
      this.toast.presentErrorToast(errorMessage || 'Hubo un error guardando categoría');
    }
  }

  async onDeleteRemoteAttachment(result: { attachment: RemoteFile; index: number }): Promise<void> {
    try {
      const { attachment, index } = result;
      this.isDeletingRemoteAttachment = true;
      const membersCategoryId = this.categoryForm.value.membersCategoryId;
      const body = {
        membersCategoryId,
        attachment
      };
      const response = await firstValueFrom(this.api.deleteMembersCategoryAttachment(body));
      this.isDeletingRemoteAttachment = false;
      this.currentAttachments.splice(index, 1);
      this.toast.presentSuccessToast(response.message);
    } catch (errorResponse: any) {
      this.isDeletingRemoteAttachment = false;
      const errorMessage = errorResponse?.error?.message;
      this.toast.presentErrorToast(errorMessage || 'Hubo un error borrando adjunto');
    }
  }
}
